import { t } from '@sportninja/common/i18n'

import type { ImportReport } from './importReport'

/** The ImportCSVBanner types a report uses. */
export type ImportReportBannerType = 'error' | 'warning' | 'success'

export interface ImportReportBanner {
  type: ImportReportBannerType
  message: string
}

export interface ImportReportNotice {
  banners: ImportReportBanner[]
  /** Offer "Review Duplicates": the problem rows are listed while the duplicates wait. */
  offerReview: boolean
}

/** The green count the import modals have always shown for a partial import. */
const importedMessage = (count: number): string =>
  `${count} item${count > 1 ? 's were' : ' was'} imported successfully.`

/**
 * SN-4838 — what the modal says about a report, above its problems table. The
 * review itself carries its own banner, so a report of duplicates alone needs
 * no notice.
 */
export const importReportNotice = (
  report: ImportReport
): ImportReportNotice => {
  switch (report.kind) {
    case 'problemsAndDuplicates':
      return {
        offerReview: true,
        banners: [
          {
            type: 'warning',
            message: [
              t('common:importReportNothingImportedYet', {}),
              t('common:importReportDuplicatesToReview', {
                count: report.duplicates.length,
              }),
              t('common:importReportRowsWillNotImport', {
                count: report.problemRowCount,
              }),
            ].join(' '),
          },
        ],
      }
    case 'nothingImported':
      return {
        offerReview: false,
        banners: [
          {
            type: 'error',
            message: t('common:importReportNothingImported', {}),
          },
        ],
      }
    case 'partlyImported':
      return {
        offerReview: false,
        banners: [
          { type: 'success', message: importedMessage(report.importedCount) },
          {
            type: 'warning',
            message: t('common:importReportRowsNotImported', {
              count: report.problemRowCount,
            }),
          },
        ],
      }
    default:
      return { offerReview: false, banners: [] }
  }
}
